/** @jsx h */
/* @jsxFrag Fragment */
import { h, Fragment } from 'preact';
import { useState, useEffect } from 'preact/hooks';
import { createPresenter } from './presenter';
import { trackDisplay, trackRedeem, trackCopyToClipboard } from './data';
import { redeem } from '../new-offer-card/actions';
import { PromotionCardCodeRedemption } from '../exclusive-offer/card/code-section';
import { track, CLICK } from '@updater-core/lib/tracker';
import './index.global.css';
import '@updater-components/card';
import '@updater-components/checkbox';
import '@updater-components/loading-spinner';

const mediaQueries = {
  isSmall: '(max-width: 767px)',
  isLarge: '(min-width: 768px)',
};

const listenOnMediaMatch = (mql, onMediaMatch) => {
  mql.addListener(onMediaMatch);
  return () => {
    mql.removeListener(onMediaMatch);
  };
};

export function useIsViewportSize(queries = mediaQueries) {
  /* Create initial mediaMatchMap with matchMedias */
  const mediaMatchMap = Object.entries(queries).reduce(
    (state, [key, query]) => ({ ...state, [key]: window.matchMedia(query) }),
    {}
  );

  /* Get the result of media match as needed */
  const createMatches = () =>
    Object.entries(mediaMatchMap).reduce(
      (state, [key, watch]) => ({ ...state, [key]: watch.matches }),
      {}
    );

  /* use the initial matches for initial state */
  const [isViewportSize, setIsViewportSize] = useState(createMatches());

  useEffect(() => {
    /* add listerners for each match and re-run createMatches as needed */
    const destroyListeners = Object.values(mediaMatchMap).map((watch) =>
      listenOnMediaMatch(watch, () => {
        setIsViewportSize(createMatches());
      })
    );

    return () => {
      destroyListeners.map((fn) => fn?.());
    };
  }, []);

  // console.log('[VIEWPORT]', isViewportSize);

  return isViewportSize;
}

const ResponsiveClassName = ({
  children,
  small = '',
  medium = small,
  large = medium,
  mediaQueries,
}) => {
  const [originalClassName] = useState(children.props.className);
  const { isSmall, isLarge } = useIsViewportSize(mediaQueries);

  let newClassName = originalClassName || '';
  if (isSmall && small) {
    newClassName = `${originalClassName} ${small}`;
  }
  if (isLarge && large) {
    newClassName = `${originalClassName} ${large}`;
  }

  return { ...children, props: { ...children.props, className: newClassName } };
};

function handleRedeemGo(promotion) {
  trackRedeem(track, promotion);
  redeem(promotion, false);
}

function handleRedeemGoAndCopy(promotion) {
  trackCopyToClipboard(track);
  trackRedeem(track, promotion);
  redeem(promotion, true);
}

export function FymSelectOfferCard({
  code,
  name,
  source,
  claimedId,
  displayName,
  discountCode,
  selected,
  entityId,
  entityLogo,
  promotion,
  promotionId,
  promotionCode,
  eligiblePromotion,
  showCode,
  showNoCode,
  showGoBtn,
  showCopyGoBtn,
  claiming,
  redemptionUrl,
  onSelectedPromo,
}) {
  return (
    <upd-card
      onClick={() => {
        if (eligiblePromotion) {
          track({
            verb: CLICK,
            object: 'fym_offers_toggle',
            domain: 'promotions',
            details: {
              promotionId,
              entityId,
              code,
              promotionCode,
              promotion,
              source: 'forward_mail',
            },
          });

          onSelectedPromo(promotionId);
        }
      }}
    >
      <div
        class={
          !eligiblePromotion
            ? 'body claimed uds-mb-2'
            : claiming
            ? 'body fym-uds-promotion-card-disabled disabled'
            : 'body '
        }
      >
        {eligiblePromotion && (
          <upd-checkbox checked={selected} disabled={claiming} />
        )}
        <div class="img-container">
          <img id="entity-logo" src={entityLogo} />
        </div>
        <div class="details">
          <div class="name">{name}</div>
          <div class="description">{displayName}</div>
        </div>
      </div>
      {!eligiblePromotion && (
        <div class="footer">
          <div class="claimed-btn-group">
            <PromotionCardCodeRedemption
              {...{
                code,
                source,
                discountCode,
                redemptionUrl,
                promotionId,
                claimedId,
                entityId,
                promotionCode,
                showCode,
                showNoCode,
                showGoBtn,
                showCopyGoBtn,
                handleRedeemGo,
                handleRedeemGoAndCopy,
              }}
            />
          </div>
        </div>
      )}
    </upd-card>
  );
}

export function FymSelectOfferCards({
  moveId,
  promos,
  noPromos,
  claiming,
  source,
  goToEOPage,
  togglePromoSelection,
  loading,
  children,
}) {
  useEffect(() => {
    trackDisplay(track, promos);
  }, [loading]);

  const confirmationHeader = noPromos
    ? 'Claim deals for your move'
    : 'Use your deals';
  const confirmationText = noPromos
    ? 'Click below to browse more offers and get special deals.'
    : 'Start using these exclusive deals.';

  const fymSelectOfferCardItems = promos
    ? promos.map((promo) => (
        <FymSelectOfferCard
          key={promo.promotion.id}
          {...createPresenter(promo, moveId)}
          claiming={claiming}
          source={source}
          onSelectedPromo={togglePromoSelection}
        />
      ))
    : '';

  return (
    <div class="fym-uds-promotion-component uds">
      <div class="fym-uds-promotion-divider" />
      <div class="fym-uds-promotion-intro">
        <div>
          <ResponsiveClassName small="uds-title-4" large="uds-title-5">
            <p>
              {source === 'fym_details' ? (
                <>Step 3: Claim Moving Discounts (optional)</>
              ) : (
                confirmationHeader
              )}
            </p>
          </ResponsiveClassName>
        </div>
        <p class="uds-body-2">
          {source === 'fym_details' ? (
            <>
              Claim exclusive savings we've picked just for you and get the
              codes sent to your inbox.
            </>
          ) : (
            <>{confirmationText}</>
          )}
        </p>
      </div>
      <div class="fym-uds-promotion-container">
        {loading ? (
          <div class="loading-promotions">
            <upd-loading-spinner />
          </div>
        ) : (
          <div class="uds-mb-4">{fymSelectOfferCardItems}</div>
        )}
        {source !== 'confirmation' && (
          <div class="uds-body-1">
            Brands you select may contact you related to this offer and your
            move.
          </div>
        )}
        {source !== 'confirmation' ? (
          children
        ) : (
          <button
            class="uds-button"
            rel="noopener"
            target="_blank"
            onClick={() => {
              // go to browse offers
              goToEOPage();
            }}
          >
            Browse more offers
          </button>
        )}
      </div>
    </div>
  );
}
